import { ModelBase, Platform } from './base';

export enum SurveyPurpose {
  SubscriptionCancellation = 'SUBSCRIPTION_CANCELLATION',
}

export enum SurveyQuestionType {
  Choice = 'CHOICE',
  Text = 'TEXT',
}

// Frontend rendering hint for Choice questions. Defaults to Radio when undefined.
// Selection-count semantics still come from minChoices / maxChoices.
export enum SurveyChoiceDisplay {
  Radio = 'RADIO',
  Dropdown = 'DROPDOWN',
  Pill = 'PILL',
  Scale = 'SCALE',
  Checkbox = 'CHECKBOX',
}

/**
 * Which save screen the cancellation flow opens when an option is chosen.
 * Set per option in the admin template editor; an option without one opens
 * nothing and the survey simply continues.
 */
export enum SurveyRetentionModal {
  /** "Let's set it up together": a setup call or the setup video, by follower count. */
  Setup = 'setup',
  /** "If it's about the price, let's fix that." */
  Price = 'price',
  /** "We heard you. Let us fix it." */
  ProductFix = 'product_fix',
}

export interface SurveyQuestionOption {
  id: string;
  title: string;
  /** The save screen choosing this option opens, if any. */
  retentionModal?: SurveyRetentionModal;
}

// Conditional visibility — question is shown only when the referenced question
// has at least one of the listed option ids selected. Frontend skips rendering
// when false; server also skips required-validation for the dependent question.
export interface SurveyVisibilityRule {
  questionId: string;
  optionIds: string[];
}

// Stored as a doc in the `questions` subcollection of a SurveyTemplate.
// Doc id IS the question id — no separate `id` field on the doc.
// minChoices / maxChoices both default to 1 when undefined (single-choice).
export interface SurveyChoiceQuestion extends ModelBase {
  type: SurveyQuestionType.Choice;
  title: string;
  required: boolean;
  sort: number;
  options: SurveyQuestionOption[];
  minChoices?: number;
  maxChoices?: number;
  display?: SurveyChoiceDisplay;
  step?: number;
  stepLabel?: string;
  visibleIf?: SurveyVisibilityRule;
}

export interface SurveyTextQuestion extends ModelBase {
  type: SurveyQuestionType.Text;
  title: string;
  required: boolean;
  sort: number;
  step?: number;
  stepLabel?: string;
  visibleIf?: SurveyVisibilityRule;
}

export type SurveyQuestion = SurveyChoiceQuestion | SurveyTextQuestion;

// surveyTemplates/{autoId}
// Lookup is by composite (purpose, key) — see firestore.indexes.json.
// Questions live in the `questions` subcollection at
// surveyTemplates/{autoId}/questions/{autoId}.
export interface SurveyTemplate extends ModelBase {
  purpose: SurveyPurpose;
  key: string;
  platform?: Platform;
  active: boolean;
  version: number;
  notes?: string;
}

// Snapshots the question content at the time of submission so historical
// reads survive template edits. `questionId` references the source question's
// Firestore doc id.
export interface AnsweredSurveyChoiceQuestion {
  questionId: string;
  type: SurveyQuestionType.Choice;
  title: string;
  required: boolean;
  sort: number;
  options: SurveyQuestionOption[];
  minChoices?: number;
  maxChoices?: number;
  display?: SurveyChoiceDisplay;
  step?: number;
  stepLabel?: string;
  visibleIf?: SurveyVisibilityRule;
  selectedOptionIds: string[];
}

export interface AnsweredSurveyTextQuestion {
  questionId: string;
  type: SurveyQuestionType.Text;
  title: string;
  required: boolean;
  sort: number;
  step?: number;
  stepLabel?: string;
  visibleIf?: SurveyVisibilityRule;
  text?: string;
}

export type AnsweredSurveyQuestion =
  | AnsweredSurveyChoiceQuestion
  | AnsweredSurveyTextQuestion;
